import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Check, Globe } from 'lucide-react';
import { useLanguage } from '@/contexts/AppContext';
import { SUPPORTED_LOCALES } from '@/config/locales';
// #457 (G5): 통화 셀렉트 UI 임시 숨김. useApp / DollarSign / currencies 번역은 정상화 시 복원.

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// #457 (G5): currency / currencies 키는 통화 UI 정상화 시 복원.
const translations = {
  ko: {
    settings: '설정',
    settingsDescription: '언어 설정을 변경할 수 있습니다.',
    language: '언어',
    apply: '적용',
  },
  es: {
    settings: 'Configuración',
    settingsDescription: 'Puedes cambiar la configuración de idioma.',
    language: 'Idioma',
    apply: 'Aplicar',
  },
  en: {
    settings: 'Settings',
    settingsDescription: 'You can change language settings.',
    language: 'Language',
    apply: 'Apply',
  }
} as const;

type TranslationKey = keyof typeof translations;

export function SettingsModal({
  isOpen,
  onClose,
}: SettingsModalProps) {
  const { locale, setLocale, language } = useLanguage();
  // language(ko/es/en)로 번역 조회, 그 외 언어는 es로 fallback
  const t = translations[language as TranslationKey] ?? translations.es;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px] bg-background border-none rounded-2xl">
        <DialogHeader>
          <DialogTitle className="text-[clamp(16px,4vw,20px)] font-bold text-foreground font-['Noto_Sans',sans-serif]">
            {t.settings}
          </DialogTitle>
          <DialogDescription className="text-[clamp(13px,3.2vw,15px)] text-muted-foreground font-['Noto_Sans',sans-serif]">
            {t.settingsDescription}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-[clamp(20px,5vw,24px)] py-4">
          {/* Language Selection */}
          <div>
            <div className="flex items-center gap-2 mb-[clamp(12px,3vw,16px)]">
              <Globe className="h-[clamp(16px,4vw,18px)] w-[clamp(16px,4vw,18px)] text-primary" />
              <h3 className="text-[clamp(14px,3.5vw,16px)] font-medium text-foreground font-['Noto_Sans',sans-serif]">
                {t.language}
              </h3>
            </div>
            <div className="space-y-[clamp(6px,1.5vw,8px)] max-h-[200px] overflow-y-auto pr-1">
              {SUPPORTED_LOCALES.map((localeInfo) => (
                <button
                  key={localeInfo.code}
                  onClick={() => setLocale(localeInfo.code)}
                  className={`w-full flex items-center justify-between p-[clamp(10px,2.5vw,12px)] rounded-xl transition-colors duration-200 ${
                    locale === localeInfo.code
                      ? 'bg-primary/10 border border-primary'
                      : 'bg-muted hover:bg-muted/80'
                  }`}
                >
                  <span className="text-[clamp(13px,3.2vw,15px)] font-medium text-foreground font-['Noto_Sans',sans-serif]">
                    {localeInfo.languageIcon} {localeInfo.nativeName}
                  </span>
                  {locale === localeInfo.code && (
                    <Check className="h-[clamp(16px,4vw,18px)] w-[clamp(16px,4vw,18px)] text-primary" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/*
            Currency Selection — #457 (G5) 임시 숨김 처리.
            환산 미완 + 결제는 MXN(현금/카드)만 지원하므로 데이터 미스매치 방지.
            환산 기능 정상화 시 복원.
          */}

          {/* Apply Button */}
          <Button
            onClick={onClose}
            className="w-full bg-primary hover:bg-primary-hover text-white rounded-xl h-[clamp(44px,11vw,48px)] text-[clamp(14px,3.5vw,16px)] font-medium font-['Noto_Sans',sans-serif]"
          >
            {t.apply}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
